import { useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Register.css";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { config } from "../Environment";
import { WSContext } from "../App";
import { directionService } from "../services/directionService";

import signInGoogle from "../images/SignInGoogle.png";
import loadingImage from "../images/loading.gif";

function Register() {
  const navigate = useNavigate();

  const location = useLocation();
  const acceptId = location.state?.acceptId;

  const displayNameRef = useRef();
  const emailRef = useRef();

  const { t } = useTranslation();

  const { socket } = useContext(WSContext);

  const [authMethodsReady, setAuthMethodsReady] = useState(false);
  const [authMethods, setAuthMethods] = useState([]);

  async function getAuthMethods() {
    const response = await fetch(config.API_BASE + "/api/auth/methods");
    if (response.ok) {
      const responseJSON = await response.json();
      if (Array.isArray(responseJSON.list)) {
        return responseJSON.list;
      }
    }
    return [];
  }

  useEffect(() => {
    (async function displayAuthMethods() {
      const result = await getAuthMethods();
      setAuthMethods(result);
      setAuthMethodsReady(true);
    })();
  }, []);

  useEffect(() => {
      if (authMethodsReady && authMethods.includes('password')) {
        displayNameRef.current.focus();
      }
  }, [authMethods, authMethodsReady]);

  useEffect(() => {
    let directionUnsubscribe = directionService
      .getDirectionChange()
      .subscribe(() => {
        setDirection(document.body.dir);
      });

    return () => {
      directionUnsubscribe.unsubscribe();
    };
  }, []);

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [direction, setDirection] = useState(document.body.dir);

  useEffect(() => {
    setErrMsg("");
    setSuccess(false);
  }, [displayName, email, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!displayName) {
      displayNameRef.current.focus();
      setLoading(false);
      setErrMsg(t("msg_error_name_required"));
      return;
    }

    if (!email || !pwd) {
      emailRef.current.focus();
      setLoading(false);
      setErrMsg(t("msg_error_email_and_password_required"));
      return;
    }

    if (pwd !== pwd2) {
      setLoading(false);
      setErrMsg(t("msg_error_passwords_dont_match"));
      return;
    }

    setLoading(true);

    let response = await fetch(config.API_BASE + "/api/auth/register", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        displayName,
        email,
        password: pwd,
      }),
      credentials: "include",
    });
    if (!response.ok) {
      setLoading(false);
      setErrMsg(t("msg_error_already_registered"));
      emailRef.current.focus();
      return;
    }

    // user successfully registered
    setDisplayName("");
    setEmail("");
    setPwd("");
    setPwd2("");
    setSuccess(true);

    const data = await response.json();
    let accessToken = data?.accessToken;

    const navState = {
      email,
      accessToken,
      from: location.state?.from,
      acceptId,
    };

    window.setTimeout(() => {
      navigate(`/login?t=${Date.now()}`, { state: navState, replace: true });
    }, 2000);
  };

  const changeDisplayName = (e) => {
    setDisplayName(e.target.value);
  };

  const changeEmail = (e) => {
    setEmail(e.target.value);
  };

  const changePassword = (e) => {
    setPwd(e.target.value);
  };

  const changePassword2 = (e) => {
    setPwd2(e.target.value);
  };

  function renderLoadingAuthMethods() {
    if (authMethodsReady) {
      return (<></>);
    }
    return (
        <div className="loadingcenter">
          <img src={loadingImage} alt={t("loading")} title={t("loading")} width="32" height="32" />
        </div>
    );
  }

  function renderRegistrationDisabled() {
    if (authMethodsReady && authMethods.length === 0) {
      return (
        <div className="warning">
          <p>Registration is temporarily disabled. Please try again later.</p>
        </div>
      );
    }
    return (<></>);
  }

  function renderRegistrationSubmitLoading() {
      <div className="loadingcenter">
        <img src={loadingImage} alt={t("loading")} title={t("loading")} width="32" height="32" />
      </div>    
  }

  function renderRegisterWithPassword() {
    if (authMethodsReady && authMethods.includes('password')) {
      return (
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="displayName">{t("display_name")}</Label>
              <Input
                id="displayName"
                type="text"
                placeholder={t("display_name")}
                onChange={changeDisplayName}
                ref={displayNameRef}
                value={displayName}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t("email_address")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                onChange={changeEmail}
                ref={emailRef}
                value={email}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                onChange={changePassword}
                value={pwd}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password2">{t("password_repeat")}</Label>
              <Input
                id="password2"
                type="password"
                onChange={changePassword2}
                value={pwd2}
                required
              />
            </div>
            {errMsg ? <div className="error">{errMsg}</div> : ""}
            {success ? (
              <div className="success">{t("msg_register_successful")}</div>
            ) : (
              ""
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {t("sign_up")}
            </Button>
            {renderRegistrationSubmitLoading()}
          </div>
        </form>
      );
    }
    return (<></>);
  }

  function renderRegisterWithGoogle() {
    if (authMethodsReady && authMethods.includes('google')) {
      return (
        <button
          className={`googlelogin${direction}`}
          onClick={() => {
            if (location.state) {
              sessionStorage.setItem(
                "locationstate",
                JSON.stringify(location.state)
              );
            }
            window.open(`${config.API_BASE}/auth/google/redirect`, "_self");
          }}
        >
          <img src={signInGoogle} alt="Google login" title="Google login" />
          {t("login_with_google")}
        </button>
      );
    }
    return (<></>);
  }

  return (
    <div className="h-full w-full flex items-center justify-start py-12">
      <div className="mx-auto grid w-[350px] gap-6">
        <div className="grid gap-2 text-center">
          <h1 className="text-3xl font-bold">{t("sign_up")}</h1>
        </div>
        {renderLoadingAuthMethods()}
        {renderRegistrationDisabled()}
        {renderRegisterWithPassword()}
        {renderRegisterWithGoogle()}
        <div className="mt-3 text-center text-sm">
          {t("already_have_an_account")}
          <Link to="/login" state={{ acceptId }} className="underline ms-2">
            {t("login")}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Register;
